import { Schema, z } from "zod/v4";
import {
  CodeTemplateGroupSchemas,
  CodeTemplatesSchemas,
  DataSourceMetadataSchema,
  DocumentationSchemas,
  ExecutionJobLogSchema,
  ExecutionLogSchema,
  ExecutionLogsStatsSchema,
  ExecutionStepLogSchema,
  GlobalVariableSchemas,
  GuardSchemas,
  IssueTrackingConfigurationSchema,
  IssueTrackingRuleSchema,
  IssueTrackingToolSchema,
  KpiSchemas,
  LocalizationSchema,
  NumberSequenceSchemas,
  ProjectSchemas,
  ReportTemplateSchemas,
  SchedulerSchemas,
  ScriptChangeLogSchemas,
  ScriptSchemas,
  SystemSchemas,
  TagSchemas,
  TokenSchemas,
  UserGroupSchemas,
  UserSchemas,
  VariableSchemas,
  VirtualUserPoolSchema,
  VirtualUserSchemaBase,
  WorkItemSchema,
  AuditTrailSchema,
} from "./ea.schema";
import {
  BaseFilterSchema,
  BaseWhereSchema,
  BoolFilterFieldSchema,
  DataTimeFilterFieldSchema,
  JsonFilterFieldSchema,
  JsonNullableFilterFieldSchema,
  NumberFilterFieldSchema,
  NumberNullableFilterFieldSchema,
  StringFilterFieldSchema,
  StringNullableFilterFieldSchema,
  generateFilterSelectSchema,
  generateFilterSortSchema,
} from "./ea.schema.query.utils";
import { ExportToCsvModels } from "./ea.enums";

export const GetRequestBaseResponseSchema = <T extends Schema>(schema: T) =>
  z.object({
    items: z.array(schema),
    meta: z.object({
      totalRowCount: z.number(),
    }),
  });

const SchedulerWhereSchema = BaseWhereSchema(() => SchedulerWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    isInternal: BoolFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    startDateTime: DataTimeFilterFieldSchema,
    endDateTime: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    recurrenceType: StringFilterFieldSchema,
  })
  .partial();

export const SchedulerFilterSchema = BaseFilterSchema.extend({
  where: SchedulerWhereSchema.optional(),
  include: z
    .object({
      groups: BoolFilterFieldSchema,
      scriptData: BoolFilterFieldSchema,
    })
    .partial()
    .optional(),
  select: generateFilterSelectSchema(SchedulerSchemas.general).optional(),
  orderBy: generateFilterSortSchema(SchedulerSchemas.general).optional(),
}).strict();

const SchedulerGroupWhereSchema = BaseWhereSchema(() => SchedulerGroupWhereSchema)
  .extend({
    schedulerJobId: NumberFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    entryName: StringFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    envName: StringFilterFieldSchema,
    startDelay: NumberFilterFieldSchema,
    runMode: StringFilterFieldSchema,
    repeatCount: NumberFilterFieldSchema,
  })
  .partial();

const SchedulerGroupIncludeSchema = z
  .object({
    relations: BoolFilterFieldSchema,
    scriptData: BoolFilterFieldSchema,
  })
  .partial();

export const SchedulerGroupFilterSchema = BaseFilterSchema.extend({
  where: SchedulerGroupWhereSchema.optional(),
  include: SchedulerGroupIncludeSchema.optional(),
}).strict();

const VirtualUsersWhereSchema = BaseWhereSchema(() => VirtualUsersWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    serverData: z.looseObject({}).passthrough(),
    systemData: z.looseObject({}).passthrough(),
    tokens: z.looseObject({}).passthrough(),
    status: StringFilterFieldSchema, // TODO convert to nativeEnum that acts like string (require new utils)
    serverAuthType: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    lastAuthenticationDate: DataTimeFilterFieldSchema,
    nextAutoAuthenticationDate: DataTimeFilterFieldSchema,
    //custom query params handled by the service
    projectId: NumberFilterFieldSchema,
    isOwner: BoolFilterFieldSchema,
  })
  .partial();

export const VirtualUsersFilterSchema = BaseFilterSchema.extend({
  where: VirtualUsersWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(VirtualUserSchemaBase)
    .extend({
      serverData: BoolFilterFieldSchema.optional(),
      systemData: BoolFilterFieldSchema.optional(),
    })
    .optional(),
  orderBy: generateFilterSortSchema(VirtualUserSchemaBase).optional(), // VirtualUserSchemas.general doesnt work because of intersected unions
}).strict();

const LocalizationWhereSchema = BaseWhereSchema(() => LocalizationWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,

    locale: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    translations: JsonFilterFieldSchema,
    needsAttention: BoolFilterFieldSchema,

    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
  })
  .partial();

export const LocalizatonFilterSchema = BaseFilterSchema.extend({
  where: LocalizationWhereSchema.optional(),
  select: generateFilterSelectSchema(LocalizationSchema).optional(),
  orderBy: generateFilterSortSchema(LocalizationSchema).optional(),
}).strict();

const UsersWhereSchema = BaseWhereSchema(() => UsersWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    language: StringFilterFieldSchema,
    username: StringFilterFieldSchema,
    email: StringFilterFieldSchema,
    sso: StringFilterFieldSchema,
    isActive: BoolFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    groups: StringFilterFieldSchema,
  })
  .partial();

const UserIncludeSchema = z
  .object({
    groups: BoolFilterFieldSchema.optional(),
    roles: BoolFilterFieldSchema.optional(),
  })
  .optional();

export const UserFilterSchema = BaseFilterSchema.extend({
  where: UsersWhereSchema.optional(),
  select: generateFilterSelectSchema(UserSchemas.general).optional(),
  orderBy: generateFilterSortSchema(UserSchemas.general).optional(),
  include: UserIncludeSchema.optional(),
}).strict();

const UserGroupsWhereSchema = BaseWhereSchema(() => UserGroupsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    //custom query params handled by the service
    projectId: NumberFilterFieldSchema,
  })
  .partial();

export const UserGroupsFilterSchema = BaseFilterSchema.extend({
  where: UserGroupsWhereSchema.optional(),
  select: generateFilterSelectSchema(UserGroupSchemas.general).optional(),
  include: z.object({ users: BoolFilterFieldSchema }).optional(),
  orderBy: generateFilterSortSchema(UserGroupSchemas.general).optional(),
}).strict();

const TokensWhereSchema = BaseWhereSchema(() => TokensWhereSchema)
  .extend({
    apiKey: BoolFilterFieldSchema,
    name: StringFilterFieldSchema,
    id: StringFilterFieldSchema,
    ttl: NumberFilterFieldSchema,
    scopes: StringNullableFilterFieldSchema,
    created: DataTimeFilterFieldSchema,
    userId: NumberNullableFilterFieldSchema.optional().nullable(),
    sso: StringNullableFilterFieldSchema,
  })
  .partial();

export const TokensFilterSchema = BaseFilterSchema.extend({
  where: TokensWhereSchema.optional(),
  select: generateFilterSelectSchema(TokenSchemas.general).optional(),
  orderBy: generateFilterSortSchema(TokenSchemas.general).optional(),
}).strict();

const ProjectsWhereSchema = BaseWhereSchema(() => ProjectsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    systemId: NumberFilterFieldSchema,
    virtualUserId: NumberFilterFieldSchema,
  })
  .partial();

const ProjectsWhereExtendedSchema = BaseWhereSchema(() => ProjectsWhereExtendedSchema)
  .extend({
    id: NumberFilterFieldSchema,
    systemId: NumberFilterFieldSchema,
    virtualUserId: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema, // TODO maybe ad more
    startDate: DataTimeFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
  })
  .partial();

export const ProjectsFilterSchema = BaseFilterSchema.extend({
  where: ProjectsWhereSchema.optional(),
  select: z
    .object({
      name: BoolFilterFieldSchema,
      integrationMetadata: BoolFilterFieldSchema,
    })
    .partial()
    .optional(),
  include: z
    .object({
      itsRules: BoolFilterFieldSchema.default(false),
    })
    .partial()
    .optional(),
}).strict();

export const ProjectsFilterExtendedSchema = BaseFilterSchema.extend({
  where: ProjectsWhereExtendedSchema.optional(),
  orderBy: generateFilterSortSchema(ProjectSchemas.general).optional(),
}).strict();

const SystemsWhereSchema = BaseWhereSchema(() => SystemsWhereSchema)
  .extend({
    envName: StringFilterFieldSchema,
    envAddress: StringFilterFieldSchema,
    envType: StringFilterFieldSchema,
    applicationName: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    userId: NumberNullableFilterFieldSchema.optional().nullable(),
    systemPlatform: StringFilterFieldSchema,
    options: JsonNullableFilterFieldSchema,
    meta: JsonNullableFilterFieldSchema,
    id: NumberFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    //custom query params handled by the service
    projectId: NumberFilterFieldSchema,
  })
  .partial();

export const SystemsFilterSchema = BaseFilterSchema.extend({
  where: SystemsWhereSchema.optional(),
  select: generateFilterSelectSchema(SystemSchemas.general).optional(),
  include: z.looseObject({}).optional(),
  orderBy: generateFilterSortSchema(SystemSchemas.general).optional(),
}).strict();

const ReportTemplatesWhereSchema = BaseWhereSchema(() => ReportTemplatesWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    originalFilename: StringFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
  })
  .partial();

export const ReportTemplatesFilterSchema = BaseFilterSchema.extend({
  where: ReportTemplatesWhereSchema.optional(),
  select: generateFilterSelectSchema(ReportTemplateSchemas.general).optional(),
  orderBy: generateFilterSortSchema(ReportTemplateSchemas.general).optional(),
}).strict();

const TaskScriptWhereSchema = BaseWhereSchema(() => TaskScriptWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    codeTemplateId: NumberFilterFieldSchema,
    withWorkItemsOnly: BoolFilterFieldSchema,
    linkedScriptId: NumberFilterFieldSchema,
    createdBy: StringFilterFieldSchema,
    envId: NumberFilterFieldSchema,
    envType: StringFilterFieldSchema,
    envName: StringFilterFieldSchema,
    startUrl: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    withoutSubProjects: BoolFilterFieldSchema,
    tagId: NumberFilterFieldSchema,
    status: StringFilterFieldSchema,
    documents: NumberFilterFieldSchema,
    datasourceId: NumberFilterFieldSchema,
    notConnectedToGlobalVariable: NumberFilterFieldSchema,
    connectedToGlobalVariable: NumberFilterFieldSchema,
    version: StringFilterFieldSchema,
  })
  .partial();

export const TaskScriptFilterSchema = BaseFilterSchema.extend({
  where: TaskScriptWhereSchema.optional(),
  orderBy: generateFilterSortSchema(
    ScriptSchemas.general.omit({
      stepCount: true,
      lastExecutorName: true,
      lastExecutionDate: true,
      lastExecutionStatus: true,
    }),
  ).optional(),
}).strict();

export type TaskScriptFilterType = z.infer<typeof TaskScriptFilterSchema>;

const DocumentationWhereSchema = BaseWhereSchema(() => DocumentationWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    prettyName: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    locale: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    type: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    version: StringFilterFieldSchema,
    signedBy: StringFilterFieldSchema,
  })
  .partial();

export const DocumentationFilterSchema = BaseFilterSchema.extend({
  where: DocumentationWhereSchema.optional(),
  include: z.object({ TaskScript: BoolFilterFieldSchema }).partial().optional(),
  select: generateFilterSelectSchema(DocumentationSchemas.general).optional(),
  orderBy: generateFilterSortSchema(DocumentationSchemas.general).optional(),
}).strict();

const ScriptChangeLogWhereSchema = BaseWhereSchema(() => ScriptChangeLogWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    scriptName: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    oldValue: StringFilterFieldSchema,
    newValue: StringFilterFieldSchema,
    user: StringFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    comment: StringFilterFieldSchema,
    signedBy: StringFilterFieldSchema,
    tagId: NumberFilterFieldSchema,
    version: StringFilterFieldSchema,
  })
  .partial();

export const ScriptChangeLogFilterSchema = BaseFilterSchema.extend({
  where: ScriptChangeLogWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(ScriptChangeLogSchemas.general).optional(),
  orderBy: generateFilterSortSchema(ScriptChangeLogSchemas.general).optional(),
}).strict();

const StepsWhereSchema = BaseWhereSchema(() => StepsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    taskScriptId: NumberFilterFieldSchema,
    lineNum: NumberFilterFieldSchema,
    averageDuration: NumberFilterFieldSchema,
  })
  .partial();

export const StepsFilterSchema = BaseFilterSchema.extend({
  where: StepsWhereSchema.optional(),
  include: z
    .object({
      linkedScripts: BoolFilterFieldSchema,
      guards: BoolFilterFieldSchema,
      codeTemplates: BoolFilterFieldSchema,
    })
    .partial()
    .optional(),
}).strict();

const StepLogsWhereSchema = BaseWhereSchema(() => StepLogsWhereSchema)
  .extend({
    lineNum: NumberFilterFieldSchema,
    label: StringFilterFieldSchema,
    executionId: NumberFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
    duration: NumberFilterFieldSchema,
    status: StringFilterFieldSchema,
    excludedLogIds: z.coerce.number().array(),
  })
  .partial();

export const StepLogsFilterSchema = BaseFilterSchema.extend({
  where: StepLogsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(ExecutionStepLogSchema).optional(),
  orderBy: generateFilterSortSchema(ExecutionStepLogSchema).optional(),
}).strict();

const VariablesWhereSchema = BaseWhereSchema(() => VariablesWhereSchema.partial())
  .extend({
    name: StringFilterFieldSchema,
    parentType: StringFilterFieldSchema,
    parentId: NumberFilterFieldSchema,
    type: StringFilterFieldSchema,
    defaultValue: StringFilterFieldSchema,
    valueType: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
  })
  .partial();

export const VariablesFilterSchema = BaseFilterSchema.extend({
  where: VariablesWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(VariableSchemas.general),
  orderBy: generateFilterSortSchema(VariableSchemas.general),
}).partial();

const GlobalVariablesWhereSchema = BaseWhereSchema(() => GlobalVariablesWhereSchema.partial())
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    value: StringFilterFieldSchema,
    type: StringFilterFieldSchema,
    valueType: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    taskScriptId: NumberFilterFieldSchema,
  })
  .partial();

export const GlobalVariablesFilterSchema = BaseFilterSchema.extend({
  where: GlobalVariablesWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(GlobalVariableSchemas.general),
  orderBy: generateFilterSortSchema(GlobalVariableSchemas.general),
}).partial();

const CodeTemplatesWhereSchema = BaseWhereSchema(() => CodeTemplatesWhereSchema.partial())
  .extend({
    name: StringFilterFieldSchema,
    functionName: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    activeVersion: NumberFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    id: NumberFilterFieldSchema,
    codeTemplateGroupId: NumberFilterFieldSchema,
  })
  .partial();

export const CodeTemplatesFilterSchema = BaseFilterSchema.extend({
  where: CodeTemplatesWhereSchema,
  include: z.object({ groups: BoolFilterFieldSchema }).partial(),
  select: generateFilterSelectSchema(CodeTemplatesSchemas["general"]),
  orderBy: generateFilterSortSchema(CodeTemplatesSchemas["general"]),
}).partial();

const DataSourceWhereSchema = BaseWhereSchema(() => DataSourceWhereSchema.partial())
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    originalFilename: StringFilterFieldSchema,
    type: StringFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
  })
  .partial();

export const DataSourcesFilterSchema = BaseFilterSchema.extend({
  where: DataSourceWhereSchema.optional(),
  include: z.object({ projectIds: BoolFilterFieldSchema }).partial(),
  select: generateFilterSelectSchema(DataSourceMetadataSchema).optional(),
  orderBy: generateFilterSortSchema(DataSourceMetadataSchema).optional(),
}).partial();

const CodeTemplateGroupsWhereSchema = BaseWhereSchema(() =>
  CodeTemplateGroupsWhereSchema.partial(),
).extend({
  name: StringFilterFieldSchema,
  description: StringFilterFieldSchema,
  updatedAt: DataTimeFilterFieldSchema,
  modifiedBy: StringFilterFieldSchema,
});

const CodeTemplateGroupsIncludeSchema = z.object({
  codeTemplates: z.coerce.boolean(),
});

export const CodeTemplateGroupsFilterSchema = BaseFilterSchema.extend({
  where: CodeTemplateGroupsWhereSchema.partial().optional(),
  include: CodeTemplateGroupsIncludeSchema.partial().optional(),
  select: generateFilterSelectSchema(CodeTemplateGroupSchemas.general).optional(),
  orderBy: generateFilterSortSchema(CodeTemplateGroupSchemas.general).optional(),
}).partial();

const NumberSequenceWhereSchema = BaseWhereSchema(() => NumberSequenceWhereSchema.partial())
  .extend({
    name: StringFilterFieldSchema,
    format: StringFilterFieldSchema,
    nextval: NumberFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
  })
  .partial();

export const NumberSequenceFilterSchema = BaseFilterSchema.extend({
  where: NumberSequenceWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(NumberSequenceSchemas.general),
  orderBy: generateFilterSortSchema(NumberSequenceSchemas.general),
}).partial();

const KpiWhereSchema = BaseWhereSchema(() => KpiWhereSchema.partial())
  .extend({
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    updatedAt: DataTimeFilterFieldSchema,
    modifiedBy: StringFilterFieldSchema,
  })
  .partial();

export const KpiFilterSchema = BaseFilterSchema.extend({
  where: KpiWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(KpiSchemas.general),
  orderBy: generateFilterSortSchema(KpiSchemas.general),
}).partial();

const GuardsWhereSchema = BaseWhereSchema(() => GuardsWhereSchema.partial())
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    type: StringFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
  })
  .partial();

const GuardsIncludeSchema = z
  .object({
    steps: z.coerce.boolean().optional(),
  })
  .optional();

export const GuardsFilterSchema = BaseFilterSchema.extend({
  where: GuardsWhereSchema,
  include: GuardsIncludeSchema,
  select: generateFilterSelectSchema(GuardSchemas.general),
  orderBy: generateFilterSortSchema(GuardSchemas.general),
}).partial();

const TagWhereSchema = BaseWhereSchema(() => TagWhereSchema.partial())
  .extend({
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
  })
  .partial();

export const TagFilterSchema = BaseFilterSchema.extend({
  where: TagWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(TagSchemas.general),
  orderBy: generateFilterSortSchema(TagSchemas.general),
}).partial();

const ExecutionLogsWhereSchema = BaseWhereSchema(() => ExecutionLogsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    jobExecutionId: StringFilterFieldSchema,
    jobId: StringFilterFieldSchema,
    schedulerJobId: NumberFilterFieldSchema,
    sessionId: StringFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    scriptName: StringFilterFieldSchema,
    scriptStatus: StringFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    envName: StringFilterFieldSchema,
    virtualUserName: StringFilterFieldSchema,
    executorName: StringFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
    duration: NumberFilterFieldSchema,
    state: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    mappedWithDevops: BoolFilterFieldSchema,
    internal: BoolFilterFieldSchema,
    withSubProjects: BoolFilterFieldSchema,
    aiHealed: BoolFilterFieldSchema,
    integrationMetadata: StringFilterFieldSchema,
    scriptVersion: StringFilterFieldSchema,
    isBackground: BoolFilterFieldSchema,
  })
  .partial();

export const ExecutionLogsFilterSchema = BaseFilterSchema.extend({
  where: ExecutionLogsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(ExecutionLogSchema).optional(),
  orderBy: generateFilterSortSchema(ExecutionLogSchema).optional(),
}).strict();

export type ExecutionLogsFilterSchemaType = z.infer<typeof ExecutionLogsFilterSchema>;

export const ExecutionLogsStatsFilterSchema = BaseFilterSchema.extend({
  where: ExecutionLogsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(ExecutionLogsStatsSchema).optional(),
  orderBy: generateFilterSortSchema(ExecutionLogsStatsSchema).optional(),
}).strict();

const ExecutionJobLogsWhereSchema = BaseWhereSchema(() => ExecutionJobLogsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    schedulerJobId: NumberFilterFieldSchema,
    successCount: NumberFilterFieldSchema,
    warningCount: NumberFilterFieldSchema,
    errorCount: NumberFilterFieldSchema,
    allCount: NumberFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
    duration: NumberFilterFieldSchema,
    state: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    mappedWithDevops: BoolFilterFieldSchema,
  })
  .partial();

export const ExecutionJobLogsFilterSchema = BaseFilterSchema.extend({
  where: ExecutionJobLogsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(ExecutionJobLogSchema).optional(),
  orderBy: generateFilterSortSchema(ExecutionJobLogSchema).optional(),
}).strict();

const IssueTrackingToolsWhereSchema = BaseWhereSchema(() => IssueTrackingToolsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    project: StringFilterFieldSchema,
    host: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
  })
  .partial();

export const IssueTrackingToolsFilterSchema = BaseFilterSchema.extend({
  where: IssueTrackingToolsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(IssueTrackingToolSchema).optional(),
  orderBy: generateFilterSortSchema(IssueTrackingToolSchema).optional(),
});

const IssueTrackingRulesWhereSchema = BaseWhereSchema(() => IssueTrackingRulesWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    itsName: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    triggers: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
  })
  .partial();

export const IssueTrackingRulesFilterSchema = BaseFilterSchema.extend({
  where: IssueTrackingRulesWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(IssueTrackingRuleSchema).optional(),
  orderBy: generateFilterSortSchema(IssueTrackingRuleSchema).optional(),
});

const IssueTrackingConfigurationsWhereSchema = BaseWhereSchema(
  () => IssueTrackingConfigurationsWhereSchema,
)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
    project: StringFilterFieldSchema,
    state: StringFilterFieldSchema,
    itsId: NumberFilterFieldSchema,
  })
  .partial();

export const IssueTrackingConfigurationsFilterSchema = BaseFilterSchema.extend({
  where: IssueTrackingConfigurationsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(IssueTrackingConfigurationSchema).optional(),
  orderBy: generateFilterSortSchema(IssueTrackingConfigurationSchema).optional(),
});

const VirtualUserPoolWhereSchema = BaseWhereSchema(() => VirtualUserPoolWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    description: StringFilterFieldSchema,
  })
  .partial();

export const VirtualUserPoolFilterSchema = BaseFilterSchema.extend({
  where: VirtualUserPoolWhereSchema.optional(),
  include: z.object({ virtualUsers: z.coerce.boolean().optional() }).optional(),
  select: generateFilterSelectSchema(VirtualUserPoolSchema).optional(),
  orderBy: generateFilterSortSchema(VirtualUserPoolSchema).optional(),
});

const WorkItemsWhereSchema = BaseWhereSchema(() => WorkItemsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    title: StringFilterFieldSchema,
    type: StringFilterFieldSchema,
    itsName: StringFilterFieldSchema,
    itsId: NumberFilterFieldSchema,
    stepLabel: StringFilterFieldSchema,
    sessionId: StringFilterFieldSchema,
    createdAt: DataTimeFilterFieldSchema,
    url: StringFilterFieldSchema,
  })
  .partial();

export const WorkItemsFilterSchema = BaseFilterSchema.extend({
  where: WorkItemsWhereSchema.optional(),
  include: z.looseObject({}).optional(),
  select: generateFilterSelectSchema(WorkItemSchema).optional(),
  orderBy: generateFilterSortSchema(WorkItemSchema).optional(),
});

const commonAuditTrailWhereProperites = {
  id: NumberFilterFieldSchema,
  tableName: StringFilterFieldSchema,
  username: StringFilterFieldSchema,
  action: StringFilterFieldSchema,
  createdAt: DataTimeFilterFieldSchema,
};

const AuditTrailWhereSchema = BaseWhereSchema(() => AuditTrailWhereSchema.partial())
  .extend({ ...commonAuditTrailWhereProperites, tstampTx: DataTimeFilterFieldSchema })
  .partial();

const AuditTrailScriptViewWhereSchema = BaseWhereSchema(() =>
  AuditTrailScriptViewWhereSchema.partial(),
)
  .extend({
    ...commonAuditTrailWhereProperites,
    scriptId: NumberFilterFieldSchema,
  })
  .partial();

const AuditTrailProjectViewWhereSchema = BaseWhereSchema(() =>
  AuditTrailProjectViewWhereSchema.partial(),
)
  .extend({
    ...commonAuditTrailWhereProperites,
    projectId: NumberFilterFieldSchema,
  })
  .partial();

export const AuditTrailFilterSchema = BaseFilterSchema.extend({
  where: AuditTrailWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(AuditTrailSchema),
  orderBy: generateFilterSortSchema(AuditTrailSchema),
}).partial();

export const AuditTrailScriptViewFilterSchema = BaseFilterSchema.extend({
  where: AuditTrailScriptViewWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(AuditTrailSchema),
  orderBy: generateFilterSortSchema(AuditTrailSchema),
}).partial();

export const AuditTrailProjectViewFilterSchema = BaseFilterSchema.extend({
  where: AuditTrailProjectViewWhereSchema,
  include: z.looseObject({}),
  select: generateFilterSelectSchema(AuditTrailSchema),
  orderBy: generateFilterSortSchema(AuditTrailSchema),
}).partial();

export const ExecutionJobLogExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.EXECUTION_JOB_LOGS),
  wsId: z.string(),
  filter: ExecutionJobLogsFilterSchema,
  meta: z.object({
    customFilename: z.string().optional(),
  }),
});

const TaskScriptExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.SCRIPTS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
    additionalFields: z.object({
      takeScreenshots: z.boolean().optional(),
      screenshotsOnlyOnInterruption: z.boolean().optional(),
      clickOnDisabledButtons: z.boolean().optional(),
      useDefaultEnvironment: z.boolean().optional(),
      topProjectName: z.boolean().optional(),
      virtualUserName: z.boolean().optional(),
    }),
  }),
  filter: z.any(),
});

const ExecutionLogExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.LOGS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
    additionalFields: z.object({
      scriptLogUrl: z.boolean().default(false),
      schedulerJobName: z.boolean().default(false),
      jobId: z.boolean().default(false),
      lastErrorMessage: z.boolean().default(false),
    }),
  }),
  filter: z.any(),
});

const ExecutionStepLogExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.STEP_LOGS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
  }),
  filter: z.any(),
});

export const ExportToCsvSchema = z.discriminatedUnion("modelName", [
  TaskScriptExportSchema,
  ExecutionLogExportSchema,
  ExecutionStepLogExportSchema,
  ExecutionJobLogExportSchema,
]);
